import type { IAppliedGeoScope, IGeoScopeRow } from "@agrigate/domain";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { useAtomValue } from "jotai";
import { isEqual } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { sessionAtom } from "../stores/session";

export const geoLevels = [
    "regionId",
    "districtId",
    "blockId",
    "clusterId",
] as const;

export type GeoLevel = (typeof geoLevels)[number];

export interface GeoComparator {
    eq?: MaybeN<string>;
    in?: MaybeN<string[]>;
}

export type GeoFilterFragment = Partial<
    Record<GeoLevel, MaybeN<GeoComparator>>
>;

export interface GeoScopeState {
    scopes: IGeoScopeRow[];
    isRestricted: boolean;
    allowedIds: Partial<Record<GeoLevel, string[]>>;
    lockedLevels: Record<GeoLevel, boolean>;
    geoFilters: GeoFilterFragment;
    pinnedIds: Partial<Record<GeoLevel, string>>;
}

const uniq = (ids: MaybeN<string>[]): string[] => [
    ...new Set(ids.filter((id): id is string => !!id)),
];

const toComparator = (ids: string[]): GeoComparator =>
    ids.length === 1 ? { eq: ids[0] } : { in: ids };

const toAllowedIds = (scopes: IGeoScopeRow[]) => {
    const allowed: Partial<Record<GeoLevel, string[]>> = {};
    for (const level of geoLevels) {
        if (!scopes.length || !scopes.every(scope => !!scope[level])) continue;
        allowed[level] = uniq(scopes.map(scope => scope[level]));
    }
    return allowed;
};

export function useGeoScope(): GeoScopeState {
    const session = useAtomValue(sessionAtom);
    const scopes = session?.geoScopes;
    return useMemo(() => {
        const rows = scopes ?? [];
        const allowedIds = toAllowedIds(rows);
        const lockedLevels = {
            regionId: allowedIds.regionId?.length === 1,
            districtId: allowedIds.districtId?.length === 1,
            blockId: allowedIds.blockId?.length === 1,
            clusterId: allowedIds.clusterId?.length === 1,
        };
        const geoFilters: GeoFilterFragment = {};
        const pinnedIds: Partial<Record<GeoLevel, string>> = {};
        for (const level of geoLevels) {
            const ids = allowedIds[level];
            if (!ids?.length) continue;
            geoFilters[level] = toComparator(ids);
            if (ids.length === 1) pinnedIds[level] = ids[0];
        }
        return {
            scopes: rows,
            isRestricted: rows.length > 0,
            allowedIds,
            lockedLevels,
            geoFilters,
            pinnedIds,
        };
    }, [JSON.stringify(scopes ?? [])]);
}

export function useAppliedGeoScope<F extends GeoFilterFragment>(
    appliedGeoScope: MaybeN<IAppliedGeoScope> | undefined,
    filters: MaybeN<F>,
    setFilters: (next: F) => void,
) {
    const setFiltersRef = useRef(setFilters);
    setFiltersRef.current = setFilters;

    useEffect(() => {
        if (!appliedGeoScope?.ids.length) return;
        const { level, ids, scopes: rows } = appliedGeoScope;

        // Derive ancestor levels (e.g. region/district for a cluster-level
        // scope) so the filters for parent levels are populated too, not
        // just the level the scope was applied at.
        const allowedIds = toAllowedIds(rows);
        allowedIds[level] = ids;

        const next = { ...(filters ?? {}) } as F;
        let changed = false;
        for (const lvl of geoLevels) {
            const lvlIds = allowedIds[lvl];
            if (lvlIds?.length) {
                // Check each level independently - a level already matching
                // (e.g. districtId set from a previous run) must not short
                // circuit ancestor levels (e.g. regionId) that are still
                // unset.
                const asked = uniq([
                    filters?.[lvl]?.eq,
                    ...(filters?.[lvl]?.in ?? []),
                ]);
                const matches =
                    asked.length && asked.every(id => lvlIds.includes(id));
                if (!matches) {
                    next[lvl] = toComparator(lvlIds) as F[typeof lvl];
                    changed = true;
                }
            }
            if (lvl === level) break;
        }
        if (!changed) return;
        if (isEqual(next, filters)) return;
        setFiltersRef.current(next);
    }, [appliedGeoScope, filters]);
}
